import {
  Color,
  FrontSide,
  NormalBlending,
  ShaderMaterial,
  type Side,
} from "three";
import { DRAG_COLORS } from "./dragTheme";
import { GLOW_VERTEX_SHADER } from "./glowPassMaterials";

/**
 * Base color of the highlight (azure). Saturated rather than pale: the glow is
 * blended over the part, so a washed-out blue would leave a light metallic
 * handle looking merely a bit brighter instead of picked out. The guides and
 * arrows are deliberately a different hue - see `dragTheme.ts`.
 */
const HIGHLIGHT_COLOR = DRAG_COLORS.part;

/**
 * Colour the glow shifts towards at the silhouette. The rim carries the
 * highlight's brightness, not just its coverage: on a dark leather grip, or
 * where the x-ray glow is blended over the case wall in front of the part,
 * more of the base azure only ever gives a darker blue, so the edge lightens
 * towards a pale sky instead and stays a bright outline on any panel.
 */
const HIGHLIGHT_RIM_COLOR = DRAG_COLORS.partRim;

/**
 * How much of the highlight colour ends up covering the part, across its flat
 * middle and at its silhouette, for the full glow. The interior is well past
 * half so the glow's colour wins over the part's own: blended more lightly, a
 * black leather grip or a dark panel showing through the x-ray glow sank the
 * azure into a navy that did not stand out from the case (the light metal
 * fittings were fine). Still short of opaque, so the screw heads and the
 * brushed metal stay legible underneath. The rim power keeps the silhouette
 * value to a narrow band at the grazing edge; a softer falloff pushes a curved
 * part like a grab handle, which is nearly all grazing surface, towards that
 * value over its whole face and swamps the material again.
 */
export const TINT_INTERIOR = 0.62;
export const TINT_SILHOUETTE = 0.95;
export const FULL_RIM_POWER = 3;

/**
 * The same figures for the outline: the mark every armed part carries all the
 * time, so it has to stay quieter than the full glow, but it is what tells
 * the user which parts the step is about, so it has to be seen at a glance -
 * a first cut at 0.08 / 0.7 was overlooked. A light interior tint the part's
 * own colour shows through, and a rim clearly short of the full glow's,
 * tightened by a higher power so it reads as an edge rather than a wash.
 * Both variants share the colours; only the coverage differs, so a part going
 * from outlined to pointed-out looks like the same mark getting stronger
 * rather than a different one.
 */
export const OUTLINE_TINT_INTERIOR = 0.22;
export const OUTLINE_TINT_SILHOUETTE = 0.85;
export const OUTLINE_RIM_POWER = 4;

/**
 * How strongly the glow is drawn: the thin `"outline"` that marks a part as
 * belonging to what the user is working on, or the `"full"` tint that marks
 * the one part under attention.
 */
export type GlowVariant = "outline" | "full";

/** The art-directed coverage of a variant, in one place for the material and its tests. */
export function glowTintTargets(variant: GlowVariant): {
  interior: number;
  silhouette: number;
  power: number;
} {
  return variant === "outline"
    ? {
        interior: OUTLINE_TINT_INTERIOR,
        silhouette: OUTLINE_TINT_SILHOUETTE,
        power: OUTLINE_RIM_POWER,
      }
    : {
        interior: TINT_INTERIOR,
        silhouette: TINT_SILHOUETTE,
        power: FULL_RIM_POWER,
      };
}

/**
 * The per-face alpha that adds up to `target` once `layers` of it have blended.
 *
 * A double-sided clone rasterises its front *and* its back faces. The hover
 * glow has always been given the alpha for two of them, which is what made the
 * double-sided handles read as solid blue when they were still drawn with the
 * depth test off; it keeps that figure so its look does not change here. The
 * x-ray glow is depth-tested against its group's nearest surface (see
 * `glowPassMaterials.ts`), so exactly one face of it reaches the screen and it
 * gets the single-layer alpha.
 */
export function layerAlpha(target: number, layers: number): number {
  return 1 - (1 - target) ** (1 / layers);
}

/**
 * Fresnel rim glow: strongest where the surface faces away from the camera, so
 * the part gets a silhouette that follows its real shape instead of a boxy
 * outline. The base term tints the whole surface, which is what makes the part
 * read as picked out rather than merely edged; the rim both thickens the tint
 * and lightens it towards `uRimColor`, so the outline is brighter than the
 * body rather than only more opaque. The vertex stage is shared with the depth
 * passes, see `GLOW_VERTEX_SHADER`.
 */
export const GLOW_FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 uColor;
  uniform vec3 uRimColor;
  uniform float uPower;
  uniform float uOpacity;
  uniform float uBase;
  varying vec3 vNormal;
  varying vec3 vView;
  void main() {
    // A part modelled as an open shell is drawn double-sided, so half of what
    // the camera sees are back faces whose normal points away from it. Left
    // as-is they would read as fully rim-lit and the silhouette would flatten
    // into a solid block, so flip them back towards the viewer.
    vec3 normal = normalize(gl_FrontFacing ? vNormal : -vNormal);
    float rim = pow(1.0 - max(dot(normal, vView), 0.0), uPower);
    float alpha = clamp((rim + uBase) * uOpacity, 0.0, 1.0);
    gl_FragColor = vec4(mix(uColor, uRimColor, rim), alpha);
    // The uniforms arrive in three's linear working space, like every other
    // colour. A ShaderMaterial writes gl_FragColor as-is, so without this
    // step the linear values land in the sRGB canvas unconverted and the
    // theme hex comes out far darker and more saturated than written -
    // dodger blue 0x1e90ff was reaching the screen as 0x0347ff.
    #include <colorspace_fragment>
  }
`;

/**
 * The tint the glow paints its mesh clones with.
 *
 * `side` mirrors the source mesh's sidedness (see `DragHighlightGlow`); `xray`
 * says whether the clone is depth-tested against the group depth the x-ray
 * passes build, which changes how many of its faces reach the screen and so
 * the alpha each one gets; `variant` picks the coverage.
 */
export function createGlowTintMaterial(
  side: Side,
  xray: boolean,
  variant: GlowVariant = "full",
): ShaderMaterial {
  // Solve the shader's `(rim + uBase) * uOpacity` for the two points that are
  // actually art-directed: the flat middle (rim 0) and the silhouette (rim 1),
  // each corrected for how many times this material blends.
  const targets = glowTintTargets(variant);
  const layers = side === FrontSide || xray ? 1 : 2;
  const interior = layerAlpha(targets.interior, layers);
  const silhouette = layerAlpha(targets.silhouette, layers);
  const opacity = silhouette - interior;

  return new ShaderMaterial({
    uniforms: {
      uColor: { value: new Color(HIGHLIGHT_COLOR) },
      uRimColor: { value: new Color(HIGHLIGHT_RIM_COLOR) },
      uPower: { value: targets.power },
      uOpacity: { value: opacity },
      uBase: { value: interior / opacity },
    },
    vertexShader: GLOW_VERTEX_SHADER,
    fragmentShader: GLOW_FRAGMENT_SHADER,
    transparent: true,
    depthWrite: false,
    // Blended over the part rather than added to it. Additive light can only
    // brighten, so it turns a dark leather grip a vivid blue but does nothing
    // to a light metallic one, which is already near white and just saturates
    // - the part ends up looking lit rather than highlighted. Alpha blending
    // tints whatever is underneath, so the same material reads on a black
    // case, a white handle and a chrome fitting alike.
    blending: NormalBlending,
    // Always depth-tested. For the x-ray glow the depth passes make sure the
    // test only ever fails behind a sibling, never behind the case, so it is
    // drawn at full strength wherever the part is - in front of the case or
    // behind it. It is the one thing that has to survive occlusion: a part the
    // user is meant to be placing must be findable without turning the case
    // around, and a dimmed ghost of it would read as a different, lesser
    // state.
    depthTest: true,
    // A flat overlay colour, not a lit surface: it is meant to appear as the
    // theme hex says, not pushed through the scene's tone curve.
    toneMapped: false,
    // The clone is tested at exactly the depth of the surface it copies: the
    // part's own, drawn by three's standard material, for the hover glow, or
    // the depth pass's for the x-ray glow. Two programs rasterising the same
    // triangle do not agree on that depth to the last bit on every GPU
    // (`invariant` in the shared vertex stage is not honoured everywhere, and
    // the standard material does not use it at all), and wherever the clone
    // lands a hair behind it loses. At a faint tint the dropped pixels went
    // unnoticed; at this coverage they showed up as hatched, striped patches
    // on flat faces, first seen on a Klappgriff on macOS. So the clone is
    // pulled one depth unit towards the camera, which turns every tie into a
    // win and is far too little to lift it past a sibling in front of it.
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    side,
  });
}
